/**
 *
 * PROJECT eVessel
 * Developed by:  3WebBox LLC - 2025
 * 
 * Disclaimer: Please make sure to read related documentation before
 * making any changes to the code. Modify the code under your own
 * responsibility. for help please contact 3WebBox.
 * 
 * https://3webbox.com  : support@3webbox.com
 * 
 * 
 */

import { ReactElement, useEffect, useState } from 'react';
import { Modal, Alert, Form, Button, Checkbox } from 'antd';
import EVAPI from '../../lib/ev_lib/main';
import { URLQuery } from '../../util/url_queries';
import { useNavigate } from 'react-router-dom';

export default function CreateContainerModal(props:any) {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [processingResults, setProcessingResults] = useState<any>(null);

  const [container, setContainer] = useState<any>({
    reference_number: '',
    destination_country: null,
    destination_state: null,
    destination_city: null
  });

  if(
    !props.selectedCargoes
    || props.selectedCargoes?.length < 1
  ) return null;

  const getCargoesUuids = async () => {
    var cargoesUuids:string = '';

    props.selectedCargoes?.map((cargo:any, index:number) => {
      if(cargo.uuid) {
        cargoesUuids += cargo.uuid + ',';
      }
    });

    // remove last comma
    if(cargoesUuids.length > 0) {
      cargoesUuids = cargoesUuids?.substring(0, cargoesUuids.length - 1);
    }

    return cargoesUuids;
  }
  
  const processSubmit = async () => {
    if(isLoading) return null;
    setIsLoading(true);

    var EV:any = new EVAPI;
    EV.debug = process.env.REACT_APP_MODE === "development" ? true : false;
    EV.baseAPI = URLQuery('targetDomain', window)||localStorage.getItem('target_domain');
    EV.authToken = localStorage.getItem('auth_token');
    
    EV.cargoes_uuids = await getCargoesUuids();

    var res:any = await EV.createContainerFromCargoesUuids();

    if(!res || res.status === 'fail') {
      setProcessingResults({
        status: 'fail',
        content: 'Failed to complete the process',
      });
    }

    else if(res.status === 'fail') {
      setProcessingResults({
        status: 'fail',
        content: res.errors,
      });
    }

    else if(res.code === 200) {
      setProcessingResults({
        status: 'success',
        content: 'Container created successfully',
        data: res.data,
      });
    }

    setIsLoading(false);
    return null;
  }

  const exportersCount = () => {
    var exportersList:string[] = [];

    props.selectedCargoes?.map((cargo:any, index:number) => {
      if(!exportersList.includes(cargo.exporters_uuid)) {
        exportersList.push(cargo.exporters_uuid);
      }
    })

    return exportersList.length;
  }

  const destinationsCount = () => {
    var destinationsList:string[] = [];

    props.selectedCargoes?.map((cargo:any, index:number) => {
      if(!destinationsList.includes(cargo.destination_city)) {
        destinationsList.push(cargo.destination_city);
      }
    })

    return destinationsList.length;
  }

  const warehousesCount = () => {
    var warehousesList:string[] = [];

    props.selectedCargoes?.map((cargo:any, index:number) => {
      if(!warehousesList.includes(cargo.warehouses_uuid)) {
        warehousesList.push(cargo.warehouses_uuid);
      }
    })

    return warehousesList.length;
  }

  const electricCargoIncluded = () => {
    var electricCargo = false;

    props.selectedCargoes?.map((cargo:any, index:number) => {
      if(cargo.fuel_type === 'electric') {
        electricCargo = true;
      }
    });

    return electricCargo;
  }
  
  return <Modal
    open={props?.open}
    title={'Create Container'}
    okButtonProps={{ style: { display: 'none' } }}
    children={<>
      {processingResults?.status === 'success' && <>
        <Alert
          message={processingResults.content}
          type={'success'}
          showIcon
          style={{marginBottom: 20}}
        />

        <Button
          type={"primary"}
          style={{ width: '100%' }}
          children={'Go to Container'}
          onClick={() => {
            navigate('/containers/containers/form/' + processingResults.data?.container_uuid)
          }}
        />
      </>}

      {processingResults?.status === 'fail' && <>
        <Alert
          message={processingResults.content}
          type={'error'}
          showIcon
          style={{marginBottom: 20}}
        />
      </>}

      {processingResults?.status !== 'success' && <>
        {/** No cargo selected error */}
        {(props.selectedCargoes?.length < 1) && <Alert
          message={'No cargoes selected'}
          type={'error'}
          showIcon
          style={{marginBottom: 20}}
        />}

        {/** Missing exporter error - cargo with missing exporter cannot be used */}
        {props.selectedCargoes?.map((cargo:any, index:number) => {
          if(!cargo || !cargo.uuid) return null;
          
          var uuidSubstr = cargo?.uuid?.substr(cargo.uuid.length - 10);
          const render = [];

          if(!cargo.exporters_uuid) {
            render.push(<Alert
              message={`Exporter not set for cargo # ${cargo.reference_id||uuidSubstr}`}
              description={`The cargo doesn't have an exporter selected. Please 
                select an exporter to be able to proceed.`}
              type={'error'}
              showIcon
              style={{marginBottom: 20}}
            />)
          }
          
          if(
            !cargo.destination_city
            || !cargo.destination_country
          ) {
            render.push(<Alert
              message={`Destination not set for cargo # ${cargo.reference_id||uuidSubstr}`}
              description={`The destination was not set for this cargo. You will still 
                be able to proceed but no destination will be set for the new`}
              type={'warning'}
              showIcon
              style={{marginBottom: 20}}
            />)
          }

          if(cargo.packing_orders_uuid) {
            render.push(<Alert
              message={`Cargo # ${cargo.reference_id||uuidSubstr} is already linked`}
              description={`The cargo is already linked to an existing packing order. 
                Proceeding will overwrite the packing order and link the cargo 
                to the newaly created one.`}
              type={'error'}
              showIcon
              style={{marginBottom: 20}}
            />)
          }

          return render;
        }) }

        {/** Number of exporters check */}
        {(exportersCount() > 1) && <Alert
          message={`Multiple exporters found`}
          description={`The selected cargoes have different exporters. This will be 
            handled as a consolidated shipment and the new container will have 
            multiple packing orders`}
          type={'warning'}
          showIcon
          style={{marginBottom: 20}}
        /> }

        {/** check destinations */}
        {(destinationsCount() > 1) && <Alert
          message={`Different destinations found`}
          description={`The selected cargoes have different destinations. You will 
            still be able to proceed but no destination will be set for the new 
            container.`}
          type={'error'}
          showIcon
          style={{marginBottom: 20}}
        /> }

        {/** check destinations */}
        {(warehousesCount() > 1) && <Alert
          message={`Different warehouses found`}
          description={`The selected cargoes are stored or processed in a different 
            warehouses. You might want to double check the cargoes warehouse. You 
            will still be able to proceed without making any changes`}
          type={'error'}
          showIcon
          style={{marginBottom: 20}}
        /> }

        {/** Electric cargo included check */}
        {(electricCargoIncluded()) && <Alert
          message={`Electric cargo included`}
          description={`One or more of the selected cargoes is electric. You will still be
            able to proceed`}
          type={'info'}
          showIcon
          style={{marginBottom: 20}}
        /> }

        <Form
          layout="vertical"
          onFinish={processSubmit}
        >
          <Form.Item
            name="confirmation"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error(
                          'You must confirm to proceed.'
                        )
                      ),
              },
            ]}
          >
            <Checkbox>I acknowledge</Checkbox>
          </Form.Item>

          <Form.Item style={{paddingTop: 10}}>
            <Button 
              type={"primary"}
              htmlType={"submit"}
              loading={isLoading}
              style={{ width: '100%' }}
            >
              {isLoading ? 'Processing...' : 'Create Container'}
            </Button>
          </Form.Item>
        </Form>
      </>}
    </>}
    cancelText={'Close'}
    onCancel={() => {
      setProcessingResults(null);
      props.onCancel();
    }}
  />
}